import { Anchor, Breadcrumbs, Grid, Text } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import SurveyForm from '../../components/Surveys/SurveyForm';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const page = `${Page['survey']}`;

const SurveysNewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fetch = useFetch();

  const [survey, setSurvey] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [loading, setLoading] = useState(false);

  const { accountId, domainId } = useParams();
  const { domain } = useSelector((state) => state.domain);

  const editor = queryString.parse(location.search).editor;

  useEffect(() => {
    const from = queryString.parse(location.search).from;
    if (!from) {
      setSurvey({
        name: 'New survey',
        config: {
          cookieName: 'rlv_survey_submitted',
        },
      });
      return;
    }

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys/${from}`)
      .then(({ survey }) => {
        survey.name = `Copy of ${survey.name}`;
        delete survey.id;
        // publicUrl is read-only and API-computed; a copy has not been saved yet, so it
        // must not ride along in the create body pointing at the source survey's link.
        delete survey.publicUrl;
        survey.externalId = uuidv4();
        setSurvey(survey);
      })
      .catch(setLoadError);
  }, [accountId, domainId, domain.senderName, fetch, editor, location.search]);

  const create = (survey) => {
    setLoading(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`, {
      method: 'POST',
      body: JSON.stringify(survey),
    })
      .then(() => {
        navigate(location.pathname.replace(`/surveys/new`, '/surveys'));
      })
      .catch(setUserError)
      .finally(() => setLoading(false));
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!survey) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/surveys` },
    { title: 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <Grid>
        <Grid.Col span={12}>
          <SurveyForm
            loading={loading}
            survey={survey}
            cancel={() => navigate(`/accounts/${accountId}/domains/${domainId}/surveys`)}
            saveSurvey={(survey) => create(survey)}
          />
        </Grid.Col>
      </Grid>
      {userError ? (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      ) : null}
    </>
  );
};

export default SurveysNewPage;
